import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { parseContentDispositionFilename } from '../core/api-response-utils';
import {
  Pageable,
  WrapperResponse,
  SearchSaleFilterSpec,
  ListSaleFilterSpec
} from './service-common';
import type {
  BillingTermsDTO,
  BillingTypeEnumValue,
  CollectionMethodEnumValue,
  CreateSaleBillingTerms,
  CreateSaleLineRequest,
  CreateSaleRequest,
  CustomerDTO,
  DaySelectionEnumValue,
  FrequencyEnumValue,
  InvoiceStatusEnumValue,
  MeasureUnitDTO,
  PreviewSaleLineRequest,
  ProductDTO,
  SaleDTO,
  SaleLineDTO,
  UpdateSaleInvoicingProfileRequest,
  SalePipelineStatusEnumValue,
  SaleProcessStatusEnumValue,
  SaleTypeEnumValue,
  SATCFDIUseDTO,
  TaxApplicationTypeEnumValue,
  TaxDTO,
  TaxProductCodeDTO,
  WrapperPageableResponseSaleDTO,
  WrapperResponseCFDIIssueResponse,
  WrapperResponseListChargeDTO,
  WrapperResponseListSaleDTO,
  WrapperResponseSaleDTO,
  WrapperResponseReadFileResponse,
  WrapperResponseListReadFileResponse,
  AttachSaleFileRequest
} from '../core/models';

export type BillingType = BillingTypeEnumValue;
export type CollectionMethod = CollectionMethodEnumValue;
export type BillingFrequency = FrequencyEnumValue;
export type DaySelection = DaySelectionEnumValue;
export type SaleStatus = InvoiceStatusEnumValue;
/** Etapa del embudo comercial (`Sale.processStatus` en API). */
export type SalePipelineStatus = SalePipelineStatusEnumValue;
/** Venta única o suscripción (`Sale.saleType` en API). */
export type SaleType = SaleTypeEnumValue;
/** Estado operativo (`Sale.status` / `SaleProcessStatus` en API). */
export type SaleProcessStatus = SaleProcessStatusEnumValue;
export type TaxApplicationType = TaxApplicationTypeEnumValue;

export { SalePipelineStatusEnum, SaleTypeEnum, SaleProcessStatusEnum } from '../core/models';

export type {
  CreateSaleBillingTerms,
  CreateSaleLineRequest,
  CreateSaleLineTaxRequest,
  CreateSaleRequest,
  PreviewSaleLineRequest,
  UpdateSaleInvoicingProfileRequest,
  SaleDTO,
  SaleLineDTO,
  TaxDTO,
  MeasureUnitDTO,
  TaxProductCodeDTO,
  ProductDTO,
  SATCFDIUseDTO,
  BillingTermsDTO,
  CustomerDTO,
  WrapperResponseSaleDTO,
  WrapperResponseListSaleDTO,
  WrapperPageableResponseSaleDTO,
  WrapperResponseCFDIIssueResponse
} from '../core/models';

@Injectable({
  providedIn: 'root'
})
export class SaleService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  /**
   * Actualiza solo el embudo comercial (`processStatus`) vía `PATCH /sales/{id}/process-status`
   * (válido para cualquier estado operativo de la venta, no solo borrador).
   */
  patchSaleProcessStatus(
    saleId: number,
    processStatus: SalePipelineStatusEnumValue
  ): Observable<WrapperResponseSaleDTO> {
    return this.http.patch<WrapperResponseSaleDTO>(`${this.apiUrl}/sales/${saleId}/process-status`, {
      processStatus
    });
  }

  searchSales(
    spec: SearchSaleFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperResponseListSaleDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperResponseListSaleDTO>(`${this.apiUrl}/sales`, { params });
  }

  listSales(
    spec: ListSaleFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseSaleDTO> {
    let params = this.buildSpecParams(spec);
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseSaleDTO>(`${this.apiUrl}/sales/list`, { params });
  }

  exportSalesCsv(
    spec: ListSaleFilterSpec = {},
    sort: string[] = ['createdAt,desc']
  ): Observable<{ blob: Blob; filename: string }> {
    let params = this.buildSpecParams(spec);
    sort.forEach((s) => {
      params = params.append('sort', s);
    });
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      params = params.append('timeZone', timeZone);
    }

    return this.http
      .get(`${this.apiUrl}/sales/export`, {
        params,
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename: parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ?? 'ventas.csv'
        }))
      );
  }

  private buildSpecParams(spec: ListSaleFilterSpec): HttpParams {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && v !== '') {
            params = params.append(key, String(v));
          }
        });
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });
    return params;
  }

  getSaleById(id: number): Observable<WrapperResponseSaleDTO> {
    return this.http.get<WrapperResponseSaleDTO>(`${this.apiUrl}/sales/${id}`);
  }

  createSale(request: CreateSaleRequest): Observable<WrapperResponseSaleDTO> {
    return this.http.post<WrapperResponseSaleDTO>(`${this.apiUrl}/sales`, request);
  }

  updateSale(id: number, request: CreateSaleRequest): Observable<WrapperResponseSaleDTO> {
    return this.http.put<WrapperResponseSaleDTO>(`${this.apiUrl}/sales/${id}`, request);
  }

  /** PUT /sales/{id}/invoicing — método/forma de pago y uso CFDI (sin estrategia de emisión). */
  updateInvoicingProfile(
    id: number,
    request: UpdateSaleInvoicingProfileRequest
  ): Observable<WrapperResponseSaleDTO> {
    return this.http.put<WrapperResponseSaleDTO>(`${this.apiUrl}/sales/${id}/invoicing`, request);
  }

  addSaleLine(saleId: number, request: CreateSaleLineRequest): Observable<WrapperResponseSaleDTO> {
    return this.http.post<WrapperResponseSaleDTO>(`${this.apiUrl}/sales/${saleId}/lines`, request);
  }

  updateSaleLine(
    saleId: number,
    lineId: number,
    request: CreateSaleLineRequest
  ): Observable<WrapperResponseSaleDTO> {
    return this.http.put<WrapperResponseSaleDTO>(
      `${this.apiUrl}/sales/${saleId}/lines/${lineId}`,
      request
    );
  }

  removeSaleLine(saleId: number, lineId: number): Observable<WrapperResponseSaleDTO> {
    return this.http.delete<WrapperResponseSaleDTO>(
      `${this.apiUrl}/sales/${saleId}/lines/${lineId}`
    );
  }

  previewSaleLine(request: PreviewSaleLineRequest): Observable<WrapperResponse<SaleLineDTO>> {
    return this.http.post<WrapperResponse<SaleLineDTO>>(
      `${this.apiUrl}/sales/lines/preview`,
      request
    );
  }

  deleteSale(id: number): Observable<WrapperResponseSaleDTO> {
    return this.http.delete<WrapperResponseSaleDTO>(`${this.apiUrl}/sales/${id}`);
  }

  /** Cancela la venta (POST /sales/{id}/cancel). */
  cancelSale(id: number, body: { cancelReason?: string } = {}): Observable<WrapperResponseSaleDTO> {
    return this.http.post<WrapperResponseSaleDTO>(`${this.apiUrl}/sales/${id}/cancel`, body);
  }

  /** Cargos asociados a la venta (GET /sales/{saleId}/charges). */
  getSaleCharges(saleId: number): Observable<WrapperResponseListChargeDTO> {
    return this.http.get<WrapperResponseListChargeDTO>(`${this.apiUrl}/sales/${saleId}/charges`);
  }

  /** Timbrado CFDI de la venta (POST /sales/{saleId}/stamp). */
  stampSale(saleId: number): Observable<WrapperResponseCFDIIssueResponse> {
    return this.http.post<WrapperResponseCFDIIssueResponse>(`${this.apiUrl}/sales/${saleId}/stamp`, {});
  }

  listSaleAttachments(
    saleId: number,
    spec: { 'name[lk]'?: string } = {}
  ): Observable<WrapperResponseListReadFileResponse> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    return this.http.get<WrapperResponseListReadFileResponse>(
      `${this.apiUrl}/sales/${saleId}/attachments`,
      { params }
    );
  }

  attachSaleFile(saleId: number, request: AttachSaleFileRequest): Observable<WrapperResponseReadFileResponse> {
    return this.http.post<WrapperResponseReadFileResponse>(
      `${this.apiUrl}/sales/${saleId}/attach`,
      request
    );
  }

  deleteSaleAttachment(saleId: number, fileId: number): Observable<{ ok: boolean; code: number; message: string; body: null }> {
    return this.http.delete<{ ok: boolean; code: number; message: string; body: null }>(
      `${this.apiUrl}/sales/${saleId}/attachments/${fileId}`
    );
  }
}
